import React, { FC } from 'react'
import { useQuery } from 'react-apollo'
import { Redirect, Route, Switch } from 'react-router-dom'
import { PrivateRoute } from 'src/ui'
import loadable from '@loadable/component'
import { useUserInfoWithRefetch } from 'src/services'
import { useLocation } from 'react-router'
import { SC_GET_WORKSPACES } from '../apollo/api'
import { isHideFeature, isWhiteLabelLogo } from '../helpers/white-label-helper'
import { WhiteLabelFeaturesEnum } from '../helpers/white-label.enum'
import {
  getCurrentWorkspaceFromLocalStorage,
  setCurrentWorkspaceToLocalStorage
} from 'src/helpers/local-storage'

const PlaylistSlidePage = loadable(async () => {
  const { PlaylistSlidePage } = await import('./playlist-slide')
  return () => <PlaylistSlidePage />
})

const UserProfilePage = loadable(async () => {
  const { UserProfilePage } = await import('./profile')
  return () => <UserProfilePage />
})

const BillingPage = loadable(async () => {
  const { BillingPage } = await import('./billing')
  return () => <BillingPage />
})

const WorkspaceMemberPage = loadable(async () => {
  const { WorkspaceMemberPage } = await import('./workspace-member')
  return () => <WorkspaceMemberPage />
})

const PlaylistsView = loadable(async () => {
  const { PlaylistsView } = await import('./playlists')
  return () => <PlaylistsView />
})

const ScheduleView = loadable(async () => {
  const { ScheduleView } = await import('./schedule')
  return () => <ScheduleView />
})

const PoPView = loadable(async () => {
  const { PoPView } = await import('./pop')
  return () => <PoPView />
})

const MyMediaPage = loadable(async () => {
  const { MyMediaPage } = await import('./mymedia')
  return () => <MyMediaPage />
})

const ConditionalContentView = loadable(async () => {
  const { ConditionalContentView } = await import('./conditional-content')
  return () => <ConditionalContentView />
})

// const ReportsView = loadable(async () => {
//   const { ReportsView } = await import('./reports')
//   return () => <ReportsView />
// })

const BackOfficeFeature = loadable(async () => {
  const { BackOfficeFeature } = await import('./back-office')
  return () => <BackOfficeFeature />
})

const AuthPageRoutes = loadable(async () => {
  const { AuthPageRoutes } = await import('./auth')
  return () => <AuthPageRoutes />
})

const TermsPage = loadable(async () => {
  const { TermsPage, SunshineTermsPage } = await import('./terms')
  return () => (isWhiteLabelLogo() === null ? <TermsPage /> : <SunshineTermsPage />)
})

const DisplaysView = loadable(async () => {
  const { DisplaysView } = await import('./displays')
  return () => <DisplaysView />
})

// const MaintenanceView = loadable(async () => {
//   const { MaintenanceView } = await import('./maintenance')
//   return () => <MaintenanceView />
// })

const PeopleCounterView = loadable(async () => {
  const { PeopleCounterView } = await import('./people-counter')
  return () => <PeopleCounterView />
})

const CarTrackerView = loadable(async () => {
  const { CarTrackerView } = await import('./car-tracker')
  return () => <CarTrackerView />
})

const DeviceManagementView = loadable(async () => {
  const { DeviceManagementView } = await import('./device-management')
  return () => <DeviceManagementView />
})

const MarketingView = loadable(async () => {
  const { MarketingView } = await import('./marketing')
  return () => <MarketingView />
})

const HelpView = loadable(async () => {
  const { HelpView } = await import('./help')
  return () => <HelpView />
})

const PlayerApplication = loadable(async () => {
  const { PlayerApplication } = await import('./downloads')
  return () => <PlayerApplication />
})

const LobbyPage = loadable(async () => {
  const { LobbyPage } = await import('./lobby')
  return () => <LobbyPage />
})

export const Routes: FC = () => {
  const location = useLocation()

  const { data, loading } = useUserInfoWithRefetch()

  const { loading: workspacesLoading, data: workspacesData } = useQuery(SC_GET_WORKSPACES, {
    fetchPolicy: 'cache-and-network',
    skip: !data?.me
  })

  let currentWorkspace = null
  let storedWorkspace = getCurrentWorkspaceFromLocalStorage()

  if (storedWorkspace && workspacesData?.workspaces) {
    const found = workspacesData.workspaces.find(w => w.id === storedWorkspace.id)

    if (!found) {
      storedWorkspace = null
    }
  }

  currentWorkspace = storedWorkspace || workspacesData?.workspaces[0]

  if (currentWorkspace) {
    setCurrentWorkspaceToLocalStorage(currentWorkspace)
  }

  if (workspacesLoading || loading) return <></>

  const isHidePaywall = isHideFeature(WhiteLabelFeaturesEnum.PAYWALL)
  const isHideDownloads = isHideFeature(WhiteLabelFeaturesEnum.DOWNLOADS)
  const isHideProduct = isHideFeature(WhiteLabelFeaturesEnum.PRODUCT_PAGE)
  const isHideAnalysis = isHideFeature(WhiteLabelFeaturesEnum.ANALYTICS_PAGE)
  const isHideHelp = isHideFeature(WhiteLabelFeaturesEnum.HELP_SECTION)
  const isHideLobby = isHideFeature(WhiteLabelFeaturesEnum.LOBBY_PAGE)
  const isHideUserAdmin = isHideFeature(WhiteLabelFeaturesEnum.USER_ADMIN_PAGE)

  return (
    <>
      <Switch>
        {currentWorkspace && data?.me?.role ? (
          <Redirect
            exact
            from={location.pathname === '/sign-in' ? '/sign-in' : '/'}
            to={
              (data?.me?.role === 'admin' || data?.me?.role === 'provider') && !isHideLobby
                ? 'lobby'
                : `/${currentWorkspace.userPath}/${currentWorkspace.workspacePath}/displays`
            }
          />
        ) : (
          <Redirect exact from="/" to="/sign-in" />
        )}
        <PrivateRoute
          component={DisplaysView}
          path='/:userPath/:workspacePath/displays'
        />
        <PrivateRoute
          component={UserProfilePage}
          path='/:userPath/:workspacePath/account'
        />
        <PrivateRoute
          component={WorkspaceMemberPage}
          path="/:userPath/:workspacePath/workspace-member"
        />
        <PrivateRoute component={PlaylistsView} path="/:userPath/:workspacePath/playlists" />
        <PrivateRoute
          component={ScheduleView}
          path="/:userPath/:workspacePath/schedule/:displayId?"
        />
        <PrivateRoute component={PoPView} path="/:userPath/:workspacePath/pop" />
        <PrivateRoute component={PoPView} path="/:userPath/:workspacePath/pop" />
        <PrivateRoute component={MyMediaPage} path="/:userPath/:workspacePath/mymedia" />
        <PrivateRoute
          component={PlaylistSlidePage}
          path="/playlist-slide/:presentationId/:slideId?"
          withLayout={false}
        />
        <Route
          component={TermsPage}
          path='/terms'
        />
        {!isHideUserAdmin && <PrivateRoute
          component={BackOfficeFeature}
          path='/:userPath/:workspacePath/back-office'
        />}
        {!isHideLobby && <PrivateRoute
          component={LobbyPage}
          path='/lobby'
        />}
        {!isHideHelp && <PrivateRoute
          component={HelpView}
          path='/:userPath/:workspacePath/help'
        />}
        {!isHidePaywall && <PrivateRoute
          component={BillingPage}
          path='/:userPath/:workspacePath/billing'
        />}
        {!isHideDownloads && <PrivateRoute
          component={PlayerApplication}
          path='/:userPath/:workspacePath/downloads'
        />}
        {!isHideProduct && <PrivateRoute
          component={MarketingView}
          path='/:userPath/:workspacePath/marketing'
        />}
        {!isHideAnalysis && <PrivateRoute
          component={PeopleCounterView}
          path='/:userPath/:workspacePath/people-counter'
        />}
        {!isHideAnalysis && <PrivateRoute
          component={CarTrackerView}
          path='/:userPath/:workspacePath/car-tracker'
        />}
        {!isHideAnalysis && (
          <PrivateRoute
            component={DeviceManagementView}
            path='/:userPath/:workspacePath/device-management'
          />
        )}
        {!isHideAnalysis && (
          <PrivateRoute
            component={ConditionalContentView}
            path="/:userPath/:workspacePath/conditional-content"
          />
        )}
        <AuthPageRoutes />
        <Redirect to="/" />
      </Switch>
    </>
  )
}
